<script setup lang="ts">
import { ChevronDown, CircleUserRound, Menu, Search, ShoppingCart, X } from 'lucide-vue-next';
import { computed, ref, watch } from 'vue';
import { RouterLink, useRoute, useRouter } from 'vue-router';

import BrandLogo from '#/components/layout/BrandLogo.vue';
import NavAiPromo from '#/components/layout/NavAiPromo.vue';
import NavLinkItem from '#/components/layout/NavLinkItem.vue';
import { useGnbMenu } from '#/composables/useGnbMenu';
import { useLogout } from '#/composables/useLogout';
import { useMobileNav } from '#/composables/useMobileNav';
import { useScrollTo } from '#/composables/useScrollTo';
import { brand, canonicalPath } from '#/config/brand';
import {
  HEADER_GROUPS,
  HEADER_UTILS,
  type NavAuth,
  type NavGroup,
  type NavLink,
  type NavUtilItem,
} from '#/config/nav';
import { useAuthStore } from '#/stores/auth';
import { useCartStore } from '#/stores/cart';

/**
 * 전역 헤더(AppHeader) — 커머스 축 GNB. 2026-10 커머스 랜딩 개편(Figma "Deolux Main Landing")의
 * **알약(pill) 헤더**로 교체했다: [로고 알약] [대분류 5개 + 대학 검색 칩이 든 메뉴 알약] [골드 로그인 알약]
 * 세 덩어리가 페이지 바탕 위에 떠 있고, 전체 메뉴는 헤더 아래 **둥근 흰 패널 1장**으로 열린다
 * (캔버스 `.kombai/canvas/commerce-landing.canvas` "전체 메뉴 · 데스크톱").
 *
 * 메뉴 정의는 전부 `config/nav.ts` SSoT(HEADER_GROUPS·HEADER_UTILS)에서 온다 — 헤더에 뜨는 항목을
 * 여기서 다시 쓰지 않는다. 동작 규약(hover/클릭 고정/외부 클릭·ESC/모바일 아코디언)은 `useGnbMenu`·
 * `useMobileNav` 그대로이며 이번 개편은 **시각만** 바꿨다.
 *
 * - 데스크톱(lg↑): 대분류 hover/focus + 클릭 고정으로 패널 열기. 열린 대분류는 알약 안에서 네이비 글자,
 *   패널 컬럼 제목은 골드 밑줄. 검색 칩은 자료 탐색(/materials/)로, 패널 상단 검색창은 `?q=` 로 보낸다.
 * - lg(980px) 미만: 로고 알약 + 둥근 햄버거. 패널은 둥근 카드(로그인·회원가입 2버튼, 검색, 대분류 아코디언).
 * - 항목 4종(route / href / section / disabled) 렌더는 `NavLinkItem` 단일 소유(`panel`·`mobile` 변형).
 * - 로그인 전 우측 버튼은 guest 유틸 2건(로그인·회원가입)을 "로그인 · 회원가입" 한 알약으로 합쳐
 *   첫 항목(로그인)으로 보낸다 — 디자인이 버튼 1개라서. 로그인 후는 장바구니·마이페이지·로그아웃.
 *
 * 색은 `--lp-*`(Figma 원본 고정값, tokens.commerce.css) — 랜딩과 같은 톤을 셸이 공유한다.
 */
const auth = useAuthStore();
const cart = useCartStore();
const route = useRoute();
const router = useRouter();
const { goToSection } = useScrollTo();
const { isOpen: isMobileOpen, close: closeMobile, toggle: toggleMobile } = useMobileNav();
const {
  openLabel,
  isOpen: isGroupOpen,
  toggle: toggleGroup,
  hoverOpen,
  hoverClose,
  hoverKeep,
  close,
} = useGnbMenu();
const { logout } = useLogout();

/** 모바일 아코디언에서 펼친 대분류(null=전부 접힘). 데스크톱 패널 상태와 독립. */
const expandedMobile = ref<string | null>(null);

function toggleMobileGroup(label: string): void {
  expandedMobile.value = expandedMobile.value === label ? null : label;
}

/** 현재 인증 수준 → auth 노출 매칭. */
function isVisible(itemAuth: NavAuth): boolean {
  if (itemAuth === 'always') return true;
  if (itemAuth === 'guest') return !auth.isAuthenticated;
  return auth.isAuthenticated; // 'authed'
}

/**
 * 노출 대상 대분류 — `hidden` 토글 → 인증 수준 순으로 거른다.
 * 하위 항목이 전부 숨겨진 대분류는 제외한다(빈 컬럼 방지).
 */
const groups = computed<NavGroup[]>(() =>
  HEADER_GROUPS.filter((g) => !g.hidden)
    .map((g) => ({ ...g, items: g.items.filter((i) => isVisible(i.auth)) }))
    .filter((g) => g.items.length > 0)
    .sort((a, b) => a.order - b.order),
);

/**
 * 컬럼 수 → Tailwind 클래스. 대분류 개수에 맞춰 그리드가 따라간다.
 * 동적 클래스 조립(`grid-cols-${n}`)은 Tailwind JIT 이 스캔하지 못하므로 **정적 맵**으로 둔다.
 */
const GRID_COLS: Record<number, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
};

/** 인증 수준으로 필터한 우측 유틸리티(order 정렬). */
const utils = computed<NavUtilItem[]>(() =>
  HEADER_UTILS.filter((i) => isVisible(i.auth)).sort((a, b) => a.order - b.order),
);

/** 비로그인 유틸(로그인·회원가입) — 데스크톱은 한 알약으로 합친다. */
const guestUtils = computed(() => utils.value.filter((i) => i.auth === 'guest' && i.route));
const guestLabel = computed(() => guestUtils.value.map((i) => i.label).join(' · '));
const guestRoute = computed(() => guestUtils.value[0]?.route ?? '/login');

/** 로그인 후 유틸(장바구니·마이페이지·로그아웃·외부 링크). */
const authedUtils = computed(() => utils.value.filter((i) => i.auth !== 'guest'));

/**
 * 현재 라우트가 속한 대분류 — 활성 표시용. route 항목만 판정한다(섹션 앵커는 스크롤 위치에 따라
 * 달라져 활성 개념이 모호). 양쪽을 `canonicalPath` 로 맞춰 trailing slash 표기 차이를 흡수한다.
 */
function isGroupActive(group: NavGroup): boolean {
  const current = canonicalPath(route.path);
  return group.items.some((i) => {
    if (!i.route || i.disabled) return false;
    const target = canonicalPath(i.route);
    return current === target || current.startsWith(target);
  });
}

/** 섹션 앵커 이동(같은 페이지=스크롤, 다른 페이지=라우팅). 이동 후 패널·모바일 메뉴를 닫는다. */
function onSectionClick(link: NavLink): void {
  if (!link.section) return;
  goToSection(link.section.path, link.section.hash);
  close();
  closeMobile();
}

/** 링크 이동 후 열린 패널 정리(RouterLink·외부 링크 공통). */
function onNavigate(): void {
  close();
  closeMobile();
}

/** 패널 상단 검색 — 자료 탐색(/materials/)의 `q` 로 넘긴다(MaterialsPage 가 초기 키워드로 읽는다). */
const searchKeyword = ref('');

function onSearch(): void {
  const q = searchKeyword.value.trim();
  void router.push({ path: '/materials/', query: q ? { q } : undefined });
  searchKeyword.value = '';
  onNavigate();
}

/** 리포트 샘플 보기 — 패널 하단 CTA(랜딩 #sample 앵커). */
function onSampleClick(): void {
  goToSection('/', '#sample');
  onNavigate();
}

/**
 * 로그아웃 액션 — 커머스는 랜딩('/')으로 복귀, 모바일 패널 닫기 동반.
 * 서버 종료→로컬 정리→이동 흐름은 useLogout() 컴포저블이 SSoT(워크스페이스 셸과 공유).
 */
function onLogout() {
  return logout('/', onNavigate);
}

// 모바일 메뉴를 닫으면 펼쳐둔 아코디언도 초기화 — 다시 열 때 이전 상태가 남지 않게 한다.
watch(isMobileOpen, (open) => {
  if (!open) expandedMobile.value = null;
});
</script>

<template>
  <header class="sticky top-0 z-header bg-lp-page-glass backdrop-blur-[10px]">
    <!-- 헤더 내부 클릭은 외부 클릭 닫기(document 리스너)로 새지 않게 차단 -->
    <div
      class="mx-auto flex h-header max-w-page items-center gap-2 px-[10px] md:px-[15px] lg:gap-[10px] xl:px-[15px]"
      @click.stop
    >
      <!-- 로고 알약 -->
      <RouterLink
        to="/"
        class="flex h-12 flex-none items-center rounded-full bg-white px-4 shadow-sm md:px-5"
        :aria-label="`${brand.name} · ${brand.group}`"
        @click="onNavigate"
      >
        <BrandLogo />
      </RouterLink>

      <!-- 메뉴 알약(lg↑): 대분류 5개 + 검색 칩 -->
      <nav
        class="hidden h-12 min-w-0 flex-1 items-center rounded-full bg-white p-[6px] shadow-sm lg:flex"
        aria-label="주 메뉴"
        @mouseleave="hoverClose"
      >
        <button
          v-for="group in groups"
          :key="group.label"
          type="button"
          class="flex h-9 min-w-0 flex-1 items-center justify-center gap-[6px] whitespace-nowrap rounded-full px-2 text-[12px] font-bold leading-none transition-colors xl:text-[13px]"
          :class="
            isGroupActive(group) || isGroupOpen(group.label)
              ? 'text-lp-navy'
              : 'text-lp-navy/70 hover:text-lp-navy'
          "
          :aria-expanded="isGroupOpen(group.label)"
          aria-haspopup="true"
          aria-controls="gnb-mega-panel"
          @mouseenter="hoverOpen(group.label)"
          @focus="hoverOpen(group.label)"
          @click="toggleGroup(group.label)"
        >
          {{ group.label }}
          <ChevronDown
            :size="12"
            :stroke-width="1.6"
            class="flex-none transition-transform"
            :class="isGroupOpen(group.label) ? 'rotate-180' : ''"
          />
        </button>
        <!-- 대학 검색 칩 — 자료 탐색 진입 -->
        <RouterLink
          to="/materials/"
          class="ml-[6px] flex h-9 w-[150px] flex-none items-center gap-[10px] rounded-full bg-lp-gray-2 px-5 text-[12px] text-muted transition-colors hover:text-lp-navy xl:w-[184px] xl:px-6"
          @click="onNavigate"
        >
          <Search :size="14" :stroke-width="1.8" class="flex-none" />
          대학 검색
        </RouterLink>
      </nav>

      <!-- 우측: 로그인(guest 1알약) / 로그인 후 유틸 / 햄버거 -->
      <div class="ml-auto flex flex-none items-center gap-2 lg:ml-0 lg:gap-[10px]">
        <RouterLink
          v-if="guestUtils.length"
          :to="guestRoute"
          class="hidden h-12 items-center justify-center gap-[10px] whitespace-nowrap rounded-full bg-lp-gold px-6 text-[12px] font-bold text-white shadow-sm transition-[filter] hover:brightness-95 lg:inline-flex xl:w-[196px]"
          @click="onNavigate"
        >
          <CircleUserRound :size="20" :stroke-width="1.7" />
          {{ guestLabel }}
        </RouterLink>

        <template v-for="item in authedUtils" :key="item.label">
          <!-- 외부 링크 -->
          <a
            v-if="item.external"
            :href="item.href"
            target="_blank"
            rel="noopener"
            class="hidden h-12 items-center whitespace-nowrap rounded-full bg-white px-5 text-[12px] font-bold text-lp-gold-deep shadow-sm lg:inline-flex"
          >
            {{ item.label }}
          </a>
          <!-- 로그아웃 액션 -->
          <button
            v-else-if="item.kind === 'logout'"
            type="button"
            class="hidden h-12 items-center whitespace-nowrap rounded-full bg-white px-5 text-[12px] font-bold text-lp-navy shadow-sm transition-colors hover:text-lp-gold-deep lg:inline-flex"
            @click="onLogout"
          >
            {{ item.label }}
          </button>
          <!-- 장바구니(아이콘 + 뱃지) — 모바일에서도 알약 옆에 노출 -->
          <RouterLink
            v-else-if="item.badge === 'cart'"
            :to="item.route!"
            class="relative inline-flex h-12 w-12 items-center justify-center rounded-full bg-white text-lp-navy shadow-sm transition-colors hover:text-lp-gold-deep"
            :aria-label="item.label"
            @click="onNavigate"
          >
            <ShoppingCart :size="20" :stroke-width="1.8" />
            <span
              v-if="cart.count > 0"
              class="absolute right-[6px] top-[6px] flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-lp-gold px-1 text-[10.5px] font-extrabold text-white"
            >
              {{ cart.count }}
            </span>
          </RouterLink>
          <!-- 마이페이지 — 골드 알약 -->
          <RouterLink
            v-else
            :to="item.route!"
            class="hidden h-12 items-center justify-center gap-[10px] whitespace-nowrap rounded-full bg-lp-gold px-6 text-[12px] font-bold text-white shadow-sm transition-[filter] hover:brightness-95 lg:inline-flex"
            @click="onNavigate"
          >
            <CircleUserRound :size="20" :stroke-width="1.7" />
            {{ item.label }}
          </RouterLink>
        </template>

        <!-- 햄버거(lg 미만) — 둥근 흰 버튼 -->
        <button
          type="button"
          class="flex h-12 w-12 items-center justify-center rounded-full bg-white text-lp-navy shadow-sm lg:hidden"
          :aria-label="isMobileOpen ? '메뉴 닫기' : '메뉴 열기'"
          :aria-expanded="isMobileOpen"
          @click="toggleMobile"
        >
          <X v-if="isMobileOpen" :size="22" :stroke-width="1.8" />
          <Menu v-else :size="22" :stroke-width="1.8" />
        </button>
      </div>
    </div>

    <!--
      전체 메뉴 패널(lg↑) — v-for 밖 단일 요소. 헤더가 sticky(=positioned)라 `top-full` 이 헤더 하단에
      붙는다. 배경은 투명·전폭이고 **카드 1장**(둥근 32px·흰색·그림자)이 헤더 알약과 같은 좌우선에 놓인다.
      패널은 헤더 안에 있어야 헤더의 stacking context 를 타고 dim(z-gnb-dim) 위로 올라간다.
    -->
    <div
      v-show="openLabel !== null"
      id="gnb-mega-panel"
      class="absolute inset-x-0 top-full hidden pt-1 lg:block"
      @mouseenter="hoverKeep"
      @mouseleave="hoverClose"
      @click.stop
    >
      <div class="mx-auto max-w-page px-[15px]">
        <div
          class="rounded-[32px] bg-white px-10 pb-8 pt-9 shadow-[0_24px_60px_rgb(16_30_60/0.16)]"
        >
          <!-- 머리: 아이브로·제목 / 검색 / 닫기 -->
          <div class="flex items-center justify-between gap-6 border-b border-line pb-6">
            <div class="min-w-0">
              <p class="text-[12px] font-bold text-lp-gold">{{ brand.name }}</p>
              <p class="mt-2 text-[24px] font-bold leading-tight tracking-[-0.02em] text-lp-navy">
                필요한 입시 정보를 한눈에
              </p>
            </div>
            <form
              class="ml-auto flex h-11 w-[232px] items-center gap-3 rounded-full bg-lp-gray px-5"
              role="search"
              @submit.prevent="onSearch"
            >
              <Search :size="16" :stroke-width="1.8" class="flex-none text-muted" />
              <label for="gnb-search" class="sr-only">대학 이름으로 리포트 검색</label>
              <input
                id="gnb-search"
                v-model="searchKeyword"
                type="search"
                placeholder="대학 이름으로 리포트 검색"
                class="min-w-0 flex-1 bg-transparent text-[13px] text-lp-navy outline-none placeholder:text-muted"
                autocomplete="off"
              />
            </form>
            <button
              type="button"
              class="flex h-9 w-9 flex-none items-center justify-center rounded-full text-lp-navy transition-colors hover:bg-lp-gray"
              aria-label="메뉴 닫기"
              @click="close"
            >
              <X :size="20" :stroke-width="1.6" />
            </button>
          </div>

          <!-- 대분류 컬럼 -->
          <div class="grid gap-8 py-8" :class="GRID_COLS[groups.length]">
            <section v-for="(group, idx) in groups" :key="`panel-${group.label}`" class="min-w-0">
              <!-- 대분류 라벨은 <p> — <h2> 로 쓰면 헤더가 페이지 제목 아웃라인에 끼어든다. -->
              <p
                :id="`gnb-col-${idx}`"
                class="border-b-2 pb-4 text-[18px] font-bold tracking-[-0.01em] transition-colors"
                :class="
                  isGroupOpen(group.label)
                    ? 'border-lp-gold text-lp-navy'
                    : 'border-lp-gold/50 text-lp-navy'
                "
              >
                {{ group.label }}
              </p>
              <ul class="mt-6 space-y-5" :aria-labelledby="`gnb-col-${idx}`">
                <li v-for="link in group.items" :key="link.label">
                  <NavLinkItem
                    :link="link"
                    variant="panel"
                    @navigate="onNavigate"
                    @section="onSectionClick"
                  />
                </li>
              </ul>
            </section>
          </div>

          <!-- 하단 CTA 스트립 -->
          <div class="flex items-center justify-between gap-6 rounded-2xl bg-lp-cream p-5">
            <div>
              <b class="text-[16px] font-bold text-lp-navy">어디서부터 시작할지 고민된다면</b>
              <p class="mt-2 text-[14px] text-muted">
                샘플로 리포트를 확인하고, 내 성적으로 지원 가능성을 살펴보세요.
              </p>
            </div>
            <button
              type="button"
              class="inline-flex h-12 flex-none items-center gap-3 rounded-full bg-lp-gold px-6 text-[14px] font-bold text-lp-navy transition-[filter] hover:brightness-95"
              @click="onSampleClick"
            >
              리포트 샘플 보기
              <svg class="h-[7px] w-[31px]" viewBox="0 0 31 7" fill="none" aria-hidden="true">
                <path
                  d="M27.37.5 30.5 3.68 27.37 6.5M30.5 3.68h-30"
                  stroke="currentColor"
                  stroke-linecap="round"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- 모바일 패널(lg 미만): 둥근 카드 — 로그인/회원가입 · 검색 · 대분류 아코디언 · 유틸리티 -->
    <nav
      v-show="isMobileOpen"
      class="absolute inset-x-0 top-header px-[10px] pt-1 md:px-[15px] lg:hidden"
      aria-label="모바일 메뉴"
      @click.stop
    >
      <div
        class="max-h-[calc(100vh-theme(spacing.header)-16px)] overflow-y-auto rounded-[24px] bg-white px-5 pb-6 pt-5 shadow-[0_24px_60px_rgb(16_30_60/0.18)]"
      >
        <p class="text-[12px] font-bold text-lp-gold">{{ brand.name }}</p>
        <p class="mt-1 text-[20px] font-bold leading-snug tracking-[-0.02em] text-lp-navy">
          필요한 입시 정보를 한눈에
        </p>

        <!-- 비로그인: 로그인 · 회원가입 2버튼 -->
        <div v-if="guestUtils.length" class="mt-5 grid grid-cols-2 gap-3">
          <RouterLink
            v-for="(item, i) in guestUtils"
            :key="`mg-${item.label}`"
            :to="item.route!"
            class="flex h-11 items-center justify-center rounded-full text-[14px] font-bold"
            :class="i === 0 ? 'bg-lp-navy text-white' : 'bg-lp-gold text-white'"
            @click="onNavigate"
          >
            {{ item.label }}
          </RouterLink>
        </div>

        <!-- 검색 -->
        <form
          class="mt-4 flex h-11 items-center gap-3 rounded-xl bg-lp-gray px-4"
          role="search"
          @submit.prevent="onSearch"
        >
          <Search :size="16" :stroke-width="1.8" class="flex-none text-muted" />
          <label for="gnb-search-m" class="sr-only">대학 이름으로 리포트 검색</label>
          <input
            id="gnb-search-m"
            v-model="searchKeyword"
            type="search"
            placeholder="대학 이름으로 리포트 검색"
            class="min-w-0 flex-1 bg-transparent text-[14px] text-lp-navy outline-none placeholder:text-muted"
            autocomplete="off"
          />
        </form>

        <!-- 대분류 아코디언 -->
        <div v-for="group in groups" :key="`m-${group.label}`" class="border-b border-line">
          <button
            type="button"
            class="flex w-full items-center justify-between py-[15px] text-left text-[16px] font-bold transition-colors"
            :class="isGroupActive(group) ? 'text-lp-gold-deep' : 'text-lp-navy'"
            :aria-expanded="expandedMobile === group.label"
            @click="toggleMobileGroup(group.label)"
          >
            <span class="inline-flex items-center gap-2">
              {{ group.label }}
              <span
                v-if="group.chip"
                class="rounded-full bg-[var(--hero-ai-green)] px-[7px] py-[2px] text-[10.5px] font-extrabold leading-none tracking-[0.02em] text-white"
                >{{ group.chip }}</span
              >
            </span>
            <ChevronDown
              :size="18"
              :stroke-width="2"
              class="transition-transform"
              :class="
                expandedMobile === group.label ? 'rotate-180 text-lp-gold' : 'text-lp-navy/60'
              "
            />
          </button>
          <div
            v-show="expandedMobile === group.label"
            class="mb-4 rounded-2xl bg-lp-cream px-4 py-2"
          >
            <NavLinkItem
              v-for="link in group.items"
              :key="`m-${link.label}`"
              :link="link"
              variant="mobile"
              @navigate="onNavigate"
              @section="onSectionClick"
            />
          </div>
        </div>

        <!-- AI 입시분석 홍보 카드 — 메뉴 라벨("내 성적 분석")은 유지하고 기능 정체를 알린다 -->
        <NavAiPromo class="mt-5" @navigate="onNavigate" />

        <!-- 로그인 후 유틸리티(같은 선언 배열 재사용) -->
        <template v-for="item in authedUtils" :key="`mu-${item.label}`">
          <a
            v-if="item.external"
            :href="item.href"
            target="_blank"
            rel="noopener"
            class="block border-b border-line py-[15px] text-[16px] font-bold text-lp-gold-deep"
            @click="onNavigate"
          >
            {{ item.label }}
          </a>
          <button
            v-else-if="item.kind === 'logout'"
            type="button"
            class="border-lp-navy/20 mt-4 flex h-11 w-full items-center justify-center rounded-full border bg-white text-[14px] font-bold text-lp-navy"
            @click="onLogout"
          >
            {{ item.label }}
          </button>
          <RouterLink
            v-else-if="item.badge === 'cart'"
            :to="item.route!"
            class="flex items-center justify-between border-b border-line py-[15px] text-[16px] font-bold text-lp-navy"
            @click="onNavigate"
          >
            <span class="inline-flex items-center gap-2">
              <ShoppingCart :size="18" :stroke-width="1.8" />
              {{ item.label }}
            </span>
            <span
              v-if="cart.count > 0"
              class="flex h-[17px] min-w-[17px] items-center justify-center rounded-full bg-lp-gold px-1 text-[10.5px] font-extrabold text-white"
            >
              {{ cart.count }}
            </span>
          </RouterLink>
          <RouterLink
            v-else
            :to="item.route!"
            class="block border-b border-line py-[15px] text-[16px] font-bold text-lp-navy"
            @click="onNavigate"
          >
            {{ item.label }}
          </RouterLink>
        </template>
      </div>
    </nav>
  </header>

  <!--
    메가메뉴 배경 dim. **`<header>` 안에 두면 동작하지 않는다** — 헤더의 backdrop-blur 가 filter 와
    똑같이 fixed 자손의 containing block 을 만들어 `fixed inset-0` 이 헤더 박스 기준이 된다. 그래서
    body 로 텔레포트한다(z-gnb-dim(70) < z-header(80) 관계는 그대로). 색은 `bg-overlay`(알파 포함 전용 토큰).
  -->
  <Teleport to="body">
    <Transition
      enter-active-class="transition-opacity duration-200 motion-reduce:transition-none"
      enter-from-class="opacity-0"
      leave-active-class="transition-opacity duration-150 motion-reduce:transition-none"
      leave-to-class="opacity-0"
    >
      <div
        v-if="openLabel !== null"
        class="fixed inset-0 z-gnb-dim hidden bg-overlay lg:block"
        aria-hidden="true"
        @click="close"
      />
    </Transition>
  </Teleport>
</template>
